import { z } from "zod";
import { Client } from "@/components/programs/ClientSelection";

// Tier form data types
export type TierFormData = {
  // Beginner fields
  firstProgram?: "yes" | "no";
  exerciseFamiliarity?: string[];
  confidenceLevel?: string;
  baselineMetrics?: string;
  equipmentPreference?: "bodyweight_only" | "freeweights" | "mixture";
  // Intermediate fields
  trainingHistory?: "less_than_6_months" | "6_to_12_months" | "1_to_2_years" | "2_plus_years";
  currentRoutine?: string;
  plateaus?: string;
  performanceMetrics?: string;
  // Advanced fields
  yearsOfTraining?: string;
  trainingSplit?: "upper_lower" | "ppl" | "body_part" | "full_body";
  recoveryCapacity?: "low" | "moderate" | "high";
  currentProgramDetails?: string;
  trainingPhilosophy?: string;
  hasPeriodizationExperience?: "yes" | "no" | "not_sure";
  periodizationApproach?: string;
  performanceTracking?: "app" | "spreadsheet" | "coach" | "dont_track";
  recentPerformance?: string;
  weaknesses?: string;
  // Athlete fields
  sport?: string;
  sportLevel?: string;
  positionRole?: string;
  seasonCycle?: "off_season" | "pre_season" | "in_season";
  sportSpecificGoals?: string;
  importantDates?: string;
  sportTrainingLoad?: string;
  sportInjuries?: string;
  keyPerformanceAreas?: string[];
  recoveryTravel?: string;
  // Preferences & Customizations fields
  periodizationType?: string;
  periodizationDescription?: string;
  likedExercises?: string[];
  dislikedExercises?: string[];
};

// Define the context shape
export interface ProgramCreationContextType {
  step: number;
  setStep: React.Dispatch<React.SetStateAction<number>>;
  clients: Client[];
  setClients: React.Dispatch<React.SetStateAction<Client[]>>;
  isLoading: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  selectedClient: Client | null;
  setSelectedClient: React.Dispatch<React.SetStateAction<Client | null>>;
  isSaving: boolean;
  setIsSaving: React.Dispatch<React.SetStateAction<boolean>>;
  handleNextStep: () => void;
  handlePreviousStep: () => void;
  fetchClients: () => Promise<void>;
  updateClientDetails: (data: z.infer<typeof clientFormSchema>) => Promise<void>;
  handleSaveProgram: (tierData: TierFormData) => Promise<void>;
}

// Client form schema used in component
export const clientFormSchema = z.object({
  name: z.string().min(2, "Full name must be at least 2 characters"),
  email: z.string().email("Invalid email address").optional().or(z.literal("")),
  phone: z.string().optional().or(z.literal("")),
  age: z.number().min(1, "Age must be at least 1").max(120, "Age must be at most 120").optional(),
  gender: z.enum(["male", "female", "other"]).optional(),
  goals: z.string().optional().or(z.literal("")),
  notes: z.string().optional().or(z.literal("")),
});

// Export tier-specific schemas for reuse
export const beginnerTierSchema = z.object({
  firstProgram: z.enum(["yes", "no"]).optional(),
  exerciseFamiliarity: z.array(z.string()).optional(),
  confidenceLevel: z.string().optional(),
  baselineMetrics: z.string().optional(),
  equipmentPreference: z.enum(["bodyweight_only", "freeweights", "mixture"]).optional(),
});

export const intermediateTierSchema = z.object({
  trainingHistory: z.enum(["less_than_6_months", "6_to_12_months", "1_to_2_years", "2_plus_years"]).optional(),
  currentRoutine: z.string().optional(),
  plateaus: z.string().optional(),
  performanceMetrics: z.string().optional(),
});

export const advancedTierSchema = z.object({
  currentProgramDetails: z.string().min(1, "Please describe their current program"),
  trainingPhilosophy: z.string().optional(),
  hasPeriodizationExperience: z.enum(["yes", "no", "not_sure"]),
  periodizationApproach: z.string().optional(),
  performanceTracking: z.enum(["app", "spreadsheet", "coach", "dont_track"]),
  recentPerformance: z.string().optional(),
  weaknesses: z.string().min(1, "Please describe their weaknesses or areas for improvement")
});

export const athleteTierSchema = z.object({
  sport: z.string().min(1, "Please specify their sport"),
  sportLevel: z.string().min(1, "Please specify their competition level"),
  positionRole: z.string().optional(),
  seasonCycle: z.enum(["off_season", "pre_season", "in_season"]),
  sportSpecificGoals: z.string().min(1, "Please describe their performance goals"),
  importantDates: z.string().optional(),
  sportTrainingLoad: z.string().min(1, "Please specify their training frequency"),
  sportInjuries: z.string().optional(),
  keyPerformanceAreas: z.array(z.string()).min(1, "Please select at least one performance area"),
  recoveryTravel: z.string().optional(),
});

// Add a new schema for preferences
export const preferencesSchema = z.object({
  periodizationType: z.string().optional(),
  periodizationDescription: z.string().optional(),
  likedExercises: z.array(z.string()).optional(),
  dislikedExercises: z.array(z.string()).optional(),
});
